import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminHealth, HealthRange } from '@traceio-org/platform-contracts';
import { classifyAdminFailure, isAbortError, type AdminFailure } from './api';

/** How often an open, visible Health page reads again (platform-api caches its DigitalOcean read about as long). */
export const HEALTH_REFRESH_MS = 30_000;
/** A read that has not answered by then is given up and counted as a failure, so it never blocks the next one. */
export const HEALTH_READ_TIMEOUT_MS = 20_000;

export interface HealthRead {
  /**
   * The last good answer. It is kept while a refresh of the same period runs and when that refresh fails (never a
   * skeleton in between). After a change of period it is the old period's answer until the new one arrives: the
   * page shows it dimmed and busy (`switching`), and a failed read of the new period clears it.
   */
  data: AdminHealth | null;
  /** The period `data` is for differs from the one asked for: the new period is loading. */
  switching: boolean;
  /** Nothing of the asked period could be read: no answer to show. */
  failure: AdminFailure | null;
  /** A refresh failed while an earlier answer of the same period is still shown. */
  refreshFailure: AdminFailure | null;
  /** A read is in flight. */
  loading: boolean;
  /** Read again now (Check again), unless a read is already in flight. */
  refresh: () => void;
}

export interface HealthReadDependencies {
  read: (range: HealthRange, refresh: boolean, signal: AbortSignal) => Promise<AdminHealth>;
  /** The page's visibility (document by default); tests hand in their own. */
  visibility?: { hidden: () => boolean; subscribe: (onChange: () => void) => () => void };
  intervalMs?: number;
  timeoutMs?: number;
}

const documentVisibility = {
  hidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  subscribe: (onChange: () => void) => {
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  },
};

/**
 * Failures no later read can fix: the session ended (token expired or refused), platform-api wants a session with MFA,
 * or the account is no longer staff. They end the page's reading until the person signs in again (see useHealthRead).
 */
const SIGN_IN_FAILURES: ReadonlySet<AdminFailure['kind']> = new Set([
  'session-ended',
  'mfa-required',
  'not-staff',
]);

export function needsSignIn(failure: AdminFailure): boolean {
  return SIGN_IN_FAILURES.has(failure.kind);
}

/** A read the hook gave up on (HEALTH_READ_TIMEOUT_MS), told apart from a read it abandoned itself. */
const timedOut = { kind: 'unavailable', reference: null } as const satisfies AdminFailure;

/**
 * Health's reads: the opening read (audited by platform-api), then a refresh every HEALTH_REFRESH_MS while the tab is
 * visible, paused while it is hidden and run at once when it becomes visible again with an answer older than that (or
 * a read in flight that long). Every later read, a change of period and Check again included, is a refresh
 * (refresh=true), which platform-api does not audit again.
 *
 * Never two reads at once: a refresh due while one is in flight is skipped, a change of period replaces it. Every
 * read gives up after HEALTH_READ_TIMEOUT_MS (a failure), so one that never settles cannot block the page.
 *
 * A read refused for the session (needsSignIn: ended, MFA required, no longer staff) is never a "last refresh failed"
 * note beside stale numbers: it replaces the answer as `failure`, so the page shows Trace Admin's standard sign-in
 * state (AdminProblem, the same as every other page, with the way back to Health), and the timer and the visibility
 * reads stop, so an ended session is not asked again every 30 seconds. Signing in again comes back through the hub
 * to a freshly loaded page, which reads as usual; a read that succeeds later (Check again) also resumes them.
 */
export function useHealthRead(range: HealthRange, deps: HealthReadDependencies): HealthRead {
  const {
    read,
    visibility = documentVisibility,
    intervalMs = HEALTH_REFRESH_MS,
    timeoutMs = HEALTH_READ_TIMEOUT_MS,
  } = deps;
  const [state, setState] = useState<Omit<HealthRead, 'refresh' | 'switching'>>({
    data: null,
    failure: null,
    refreshFailure: null,
    loading: true,
  });
  // The session can no longer read (needsSignIn): no timer and no visibility reads until a read succeeds again.
  const [stopped, setStopped] = useState(false);
  const inFlight = useRef<{ controller: AbortController; startedAt: number } | null>(null);
  const opened = useRef(false);
  const lastAnswerAt = useRef(0);
  const readRef = useRef(read);
  readRef.current = read;
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const run = useCallback(
    (options: { force: boolean }) => {
      // One read at a time. A new period (force) replaces a read of the old one; a timer tick waits its turn.
      if (inFlight.current && !options.force) return;
      inFlight.current?.controller.abort();
      const controller = new AbortController();
      const current = { controller, startedAt: Date.now() };
      inFlight.current = current;
      const asked = rangeRef.current;
      const refresh = opened.current;
      let gaveUp = false;
      const settle = () => {
        window.clearTimeout(timer);
        if (inFlight.current === current) inFlight.current = null;
      };
      const fail = (failure: AdminFailure) => {
        if (needsSignIn(failure)) {
          setStopped(true);
          setState({ data: null, failure, refreshFailure: null, loading: false });
          return;
        }
        setState((now) =>
          // A failed refresh of the period on screen keeps it; a failed read of another period clears the old
          // period's numbers, which must never stand under the new period's name.
          now.data && now.data.range === asked
            ? { ...now, refreshFailure: failure, loading: false }
            : { data: null, failure, refreshFailure: null, loading: false },
        );
      };
      // Given up at the timeout, whether or not the read honours its signal: the page moves on at once.
      const timer = window.setTimeout(() => {
        // Only the read in flight gives up: a superseded read, or one left by an unmounted page, never touches state.
        if (inFlight.current !== current) return;
        gaveUp = true;
        settle();
        controller.abort();
        fail(timedOut);
      }, timeoutMs);
      setState((now) => ({ ...now, loading: true }));
      readRef.current(asked, refresh, controller.signal).then(
        (data) => {
          settle();
          if (controller.signal.aborted) return;
          opened.current = true;
          lastAnswerAt.current = Date.now();
          setStopped(false);
          setState({ data, failure: null, refreshFailure: null, loading: false });
        },
        (error: unknown) => {
          if (gaveUp) return;
          settle();
          if (controller.signal.aborted || isAbortError(error)) {
            // Abandoned for a newer read, which owns the loading state; abandoned with none after it, the page is idle.
            if (inFlight.current === null) setState((now) => ({ ...now, loading: false }));
            return;
          }
          fail(classifyAdminFailure(error));
        },
      );
    },
    [timeoutMs],
  );

  // The opening read, and a read of each new period.
  useEffect(() => {
    run({ force: true });
  }, [range, run]);

  // Refresh while visible; on becoming visible, read at once if the answer is older than one interval, replacing a
  // read in flight that long.
  useEffect(() => {
    if (stopped) return;
    let timer: number | undefined;
    const schedule = () => {
      window.clearInterval(timer);
      if (visibility.hidden()) return;
      timer = window.setInterval(() => run({ force: false }), intervalMs);
    };
    schedule();
    const unsubscribe = visibility.subscribe(() => {
      if (!visibility.hidden() && opened.current && Date.now() - lastAnswerAt.current >= intervalMs) {
        const stuck = inFlight.current !== null && Date.now() - inFlight.current.startedAt >= intervalMs;
        run({ force: stuck });
      }
      schedule();
    });
    return () => {
      window.clearInterval(timer);
      unsubscribe();
    };
  }, [intervalMs, run, visibility, stopped]);

  useEffect(
    () => () => {
      inFlight.current?.controller.abort();
      inFlight.current = null;
    },
    [],
  );

  const refresh = useCallback(() => run({ force: false }), [run]);
  const switching = state.data !== null && state.data.range !== range;
  return { ...state, switching, refresh };
}
